import React, { useEffect, useState } from 'react';
import { Table, Card, Button, Form, Input, Select, Space, Modal, Typography, Tag, message } from 'antd';
import Layout from '../../../components/Layout';
import { customBaseUrl, httpGet, httpPost } from '../../../store/http';
import { showLoader, hideLoader } from '../../../utils/loader';
import Swal from 'sweetalert2';

const { Title } = Typography;
const { Option } = Select;

const OtpManagement = () => {
    const [exceededUsers, setExceededUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isResetModalVisible, setIsResetModalVisible] = useState(false);
    const [form] = Form.useForm();

    const fetchExceededLimits = async () => {
        setLoading(true);
        showLoader();
        try {
            const res = await httpGet(`${customBaseUrl.authUrl}/otp/admin/exceeded-limits`);
            if (res && Array.isArray(res)) {
                setExceededUsers(res.map((user, index) => ({ ...user, key: index })));
            } else if (res && res.success && Array.isArray(res.data)) {
                setExceededUsers(res.data.map((user, index) => ({ ...user, key: index })));
            } else {
                setExceededUsers([]);
                // message.error('Failed to fetch users with exceeded limits');
            }
        } catch (error) {
            console.error('Error fetching exceeded limits:', error);
            message.error('An error occurred while fetching data');
        } finally {
            setLoading(false);
            hideLoader();
        }
    };

    useEffect(() => {
        fetchExceededLimits();
    }, []);

    const handleResetAttempts = async (identifier, purpose) => {
        const result = await Swal.fire({
            title: 'Reset OTP Attempts?',
            text: `Are you sure you want to reset ${purpose} OTP attempts for ${identifier}?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, reset it!',
            cancelButtonText: 'No, cancel',
        });

        if (result.isConfirmed) {
            showLoader();
            try {
                const res = await httpPost(
                    `${customBaseUrl.authUrl}/api/v1/admin/users/otp/reset-attempts?identifier=${encodeURIComponent(identifier)}&purpose=${purpose}`,
                    {},
                    false,
                    null,
                    false
                );

                if (res && res.success) {
                    Swal.fire('Reset!', res.message || 'OTP attempts have been reset successfully.', 'success');
                    fetchExceededLimits();
                } else {
                    Swal.fire('Failed', res?.message || 'Failed to reset OTP attempts.', 'error');
                }
            } catch (error) {
                console.error('Error resetting OTP attempts:', error);
                Swal.fire('Error', 'An error occurred while resetting OTP attempts.', 'error');
            } finally {
                hideLoader();
            }
        }
    };

    const handleResetAllAttempts = async (identifier) => {
        const result = await Swal.fire({
            title: 'Reset ALL OTP Attempts?',
            text: `Are you sure you want to reset ALL OTP attempts for ${identifier}? This will clear blocks for all purposes (LOGIN, PASSWORD_RESET, etc.)`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, reset all!',
            cancelButtonText: 'No, cancel',
        });

        if (result.isConfirmed) {
            showLoader();
            try {
                const res = await httpPost(
                    `${customBaseUrl.authUrl}/api/v1/admin/users/otp/reset-all-attempts?identifier=${encodeURIComponent(identifier)}`,
                    {},
                    false,
                    null,
                    false
                );

                if (res && res.success) {
                    Swal.fire('Reset!', res.message || 'All OTP attempts have been reset successfully.', 'success');
                    fetchExceededLimits();
                } else {
                    Swal.fire('Failed', res?.message || 'Failed to reset all OTP attempts.', 'error');
                }
            } catch (error) {
                console.error('Error resetting all OTP attempts:', error);
                Swal.fire('Error', 'An error occurred while resetting all OTP attempts.', 'error');
            } finally {
                hideLoader();
            }
        }
    };

    const showManualResetModal = () => {
        setIsResetModalVisible(true);
    };

    const handleManualReset = () => {
        form.validateFields().then(async (values) => {
            setIsResetModalVisible(false);
            if (values.purpose === 'ALL') {
                await handleResetAllAttempts(values.identifier);
            } else {
                await handleResetAttempts(values.identifier, values.purpose);
            }
            form.resetFields();
        });
    };


    const columns = [
        {
            title: 'Identifier',
            dataIndex: 'identifier',
            key: 'identifier',
        },
        {
            title: 'Purpose',
            dataIndex: 'purpose',
            key: 'purpose',
            render: (text) => <Tag color="blue">{text}</Tag>,
        },
        {
            title: 'Action',
            key: 'action',
            render: (_, record) => (
                <Space size="middle">
                    <Button
                        type="primary"
                        danger
                        size="small"
                        onClick={() => handleResetAttempts(record.identifier, record.purpose)}
                    >
                        Reset Purpose
                    </Button>
                    <Button
                        type="default"
                        danger
                        size="small"
                        onClick={() => handleResetAllAttempts(record.identifier)}
                    >
                        Reset All
                    </Button>
                </Space>
            ),
        },
    ];

    return (
        <Layout>
            <div style={{ padding: '24px' }}>
                <Card
                    title={
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Title level={4} style={{ margin: 0 }}>OTP Management</Title>
                            <Button type="primary" onClick={showManualResetModal}>Manual Reset</Button>
                        </div>
                    }
                >
                    <Typography.Paragraph>
                        The following users have exceeded the maximum allowed OTP attempts and are currently blocked from requesting new OTPs.
                    </Typography.Paragraph>
                    <Table
                        columns={columns}
                        dataSource={exceededUsers}
                        loading={loading}
                        pagination={{ pageSize: 10 }}
                        locale={{ emptyText: 'No users with exceeded OTP limits found.' }}
                    />
                </Card>
            </div>

            <Modal
                title="Manual Reset OTP Attempts"
                visible={isResetModalVisible}
                onOk={handleManualReset}
                onCancel={() => setIsResetModalVisible(false)}
                okText="Reset Attempts"
            >
                <Form form={form} layout="vertical">
                    <Form.Item
                        name="identifier"
                        label="Identifier (Email or Phone)"
                        rules={[{ required: true, message: 'Please input the identifier!' }]}
                    >
                        <Input placeholder="e.g. user@example.com or 2348000000000" />
                    </Form.Item>
                    <Form.Item
                        name="purpose"
                        label="Purpose"
                        rules={[{ required: true, message: 'Please select a purpose!' }]}
                    >
                        <Select placeholder="Select OTP Purpose">
                            <Option value="ALL">ALL</Option>
                            <Option value="LOGIN">LOGIN</Option>
                            <Option value="PASSWORD_RESET">PASSWORD_RESET</Option>
                            <Option value="PIN_RESET">PIN_RESET</Option>
                            <Option value="REGISTRATION">REGISTRATION</Option>
                            <Option value="HMAC_REISSUE">HMAC_REISSUE</Option>
                            <Option value="DEVICE_CHANGE">DEVICE_CHANGE</Option>
                        </Select>
                    </Form.Item>
                </Form>
            </Modal>
        </Layout>
    );
};

export default OtpManagement;
